/**
 * Aufwärmen der API (Kaltstart der DEV-Container-App, ca. 30 s).
 *
 * Reine Zustandslogik ohne DOM und React, damit sie testbar ist; die Zeitgrenzen sind
 * Parameter. Ablauf: Antwortet `/core/health` nicht innerhalb von `sofortMs`, gilt die
 * API als „startet“ und wird alle `abfrageMs` erneut gefragt, bis sie antwortet oder
 * `abbruchMs` verstrichen sind. Antwortet sie sofort, bleibt der Zustand unauffällig.
 */
export type ApiStatus = 'unbekannt' | 'startet' | 'bereit' | 'fehler';

export interface AufwaermGrenzen {
  /** Antwort bis hierhin gilt als „sofort“ — keine Anzeige. */
  sofortMs: number;
  /** Abstand der Wiederholungen im Zustand „startet“. */
  abfrageMs: number;
  /** Nach dieser Zeit ohne Antwort: Fehler. */
  abbruchMs: number;
  /** „API bereit“ bleibt so lange sichtbar. */
  bereitAnzeigeMs: number;
  /** Abstand der Keepalive-Abfragen bei sichtbarem Dokument. */
  keepaliveMs: number;
}

export const STANDARD_GRENZEN: AufwaermGrenzen = {
  sofortMs: 1500,
  abfrageMs: 3000,
  abbruchMs: 120_000,
  bereitAnzeigeMs: 3000,
  keepaliveMs: 4 * 60_000,
};

export interface AufwaermerOptionen {
  /** Fragt die API einmal; `true` = Antwort erhalten. Darf nicht werfen. */
  pruefen: () => Promise<boolean>;
  bei: (status: ApiStatus) => void;
  grenzen?: Partial<AufwaermGrenzen>;
}

export interface Aufwaermer {
  /** Aufwärmen starten; läuft bereits eines, passiert nichts. */
  aufwaermen: () => Promise<void>;
  /** Dokument sichtbar/verborgen: steuert den Keepalive. */
  sichtbar: (sichtbar: boolean) => void;
  stop: () => void;
}

const schlafen = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export function erzeugeAufwaermer(opt: AufwaermerOptionen): Aufwaermer {
  const g: AufwaermGrenzen = { ...STANDARD_GRENZEN, ...opt.grenzen };
  let gestoppt = false;
  let laeuft: Promise<void> | null = null;
  let keepalive: ReturnType<typeof setInterval> | null = null;
  let bereitTimer: ReturnType<typeof setTimeout> | null = null;
  let letzteAntwort = 0;

  const melden = (s: ApiStatus) => {
    if (!gestoppt) opt.bei(s);
  };

  async function lauf(): Promise<void> {
    const beginn = Date.now();
    let startet = false;
    // Nach `sofortMs` ohne Antwort die Anzeige einschalten, die Abfrage läuft weiter.
    const sofortTimer = setTimeout(() => {
      if (startet) return;
      startet = true;
      melden('startet');
    }, g.sofortMs);
    try {
      for (;;) {
        const ok = await opt.pruefen();
        if (gestoppt) return;
        if (ok) {
          letzteAntwort = Date.now();
          if (startet) {
            melden('bereit');
            bereitTimer = setTimeout(() => melden('unbekannt'), g.bereitAnzeigeMs);
          } else melden('unbekannt');
          return;
        }
        if (Date.now() - beginn >= g.abbruchMs) {
          melden('fehler');
          return;
        }
        if (!startet) {
          startet = true;
          clearTimeout(sofortTimer);
          melden('startet');
        }
        await schlafen(g.abfrageMs);
        if (gestoppt) return;
      }
    } finally {
      clearTimeout(sofortTimer);
    }
  }

  function aufwaermen() {
    if (!laeuft) {
      if (bereitTimer) clearTimeout(bereitTimer);
      laeuft = lauf().finally(() => {
        laeuft = null;
      });
    }
    return laeuft;
  }

  function sichtbar(ist: boolean) {
    if (gestoppt) return;
    if (!ist) {
      if (keepalive) clearInterval(keepalive);
      keepalive = null;
      return;
    }
    // Rückkehr nach langer Pause: die API ist vermutlich eingeschlafen.
    if (Date.now() - letzteAntwort >= g.keepaliveMs) void aufwaermen();
    if (!keepalive) keepalive = setInterval(() => void aufwaermen(), g.keepaliveMs);
  }

  function stop() {
    gestoppt = true;
    if (keepalive) clearInterval(keepalive);
    if (bereitTimer) clearTimeout(bereitTimer);
    keepalive = null;
  }

  return { aufwaermen, sichtbar, stop };
}
